import { type Dispatch, type FC, type SetStateAction, useEffect, useState } from 'react';

import { useSession } from '@eneco/auth';
import RichText from '@eneco/components/RichText/RichText';
import useDC from '@eneco/dc/useDC';
import { typedYupResolver } from '@eneco/hooks/form';
import { Dialog, InputSelect, InputText, NotificationBox, Stack, Text } from '@eneco/sparky';
import { DC_Domain_Models_Products_DiscontinueDisallowReason } from '@monorepo-types/dc';
import { useMachine } from '@xstate/react';
import { signIn } from 'next-auth/react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import type { CancellationReasonStepContent } from './cancellation-reason-step-content.types';
import { Header, Layout, NextButton, Overview } from '../../components';
import { useFlowRedirect, usePageContent } from '../../hooks';
import { createCancellationReasonMachine } from '../../machines/cancellationReasonMachine';
import { createSmartThermostatCancellationReasonMachine } from '../../machines/smartThermostatCancellationReasonMachine';
import { getDialogContent } from '../../stepsSharedComponents/cancellation/getDialogContent';
import { FlowContext } from '../../types';
import { useFlowHooks } from '../../utils/FlowProvider';

const OTHER_REASON_VALUE = 'ANL';
const DECEASE_REASON_VALUE = 'OVL';

type ReasonMachine =
  | ReturnType<typeof createCancellationReasonMachine>
  | ReturnType<typeof createSmartThermostatCancellationReasonMachine>;

interface FormData {
  reason: string;
  explanation?: string;
}

export interface CancellationReasonSubmitPayload {
  reason: string;
  reasonLabel: string;
  explanation?: string;
  intake: unknown;
}

export type CancellationReasonDisallowMode = 'dialog' | 'notification' | 'ignore';
export type CancellationReasonUnauthenticatedAction = 'okta' | 'login-route';

export interface CancellationReasonStepBaseProps {
  textualData?: CancellationReasonStepContent;
  reasonMachine: ReasonMachine;
  unauthenticatedAction: CancellationReasonUnauthenticatedAction;
  enableDeceasedReason?: boolean;
  disallowMode: CancellationReasonDisallowMode;
  onSuccessfulSubmit: (payload: CancellationReasonSubmitPayload) => void;
}

const getNotificationDisallowContent = (
  textualData: CancellationReasonStepContent | undefined,
  disallowReason: string | undefined,
) => {
  switch (disallowReason) {
    case 'noActiveProduct':
      return textualData?.dialogs?.noActiveProductDialog?.value;
    case 'cancelledAlready':
      return textualData?.dialogs?.cancelledAlreadyDialog?.value;
    case 'minimumContractPeriod':
      return textualData?.dialogs?.minimumContractPeriodNotMetDialog?.value;
    case 'moreThanOneMatchedProduct':
      return textualData?.dialogs?.multipleServiceContractsActiveDialog?.value;
    default:
      return textualData?.notification?.errorNotification?.value;
  }
};

const DisallowDialog: FC<{
  title?: string;
  content?: string;
  triggerText?: string;
  isOpen: boolean;
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
}> = ({ title, content, triggerText, isOpen, setDialogOpen }) => {
  const { customRedirect } = useFlowRedirect();
  const { flowCancelationUrl } = usePageContent();

  const closeDialog = () => {
    setDialogOpen(false);
    // Flat content reaches the href directly. The narrowing this replaces was
    // needed because the Sitecore field type declared `value` as a string
    // while the payload actually carried a link object.
    customRedirect(flowCancelationUrl?.href ?? '/');
  };

  return (
    <Dialog
      description={triggerText}
      setOpen={setDialogOpen}
      onClose={closeDialog}
      title={title}
      isOpen={isOpen}
      width="regular">
      <Text>
        <RichText html={content} />
      </Text>
    </Dialog>
  );
};

export const CancellationReasonStepBase: FC<CancellationReasonStepBaseProps> = ({
  textualData,
  reasonMachine,
  unauthenticatedAction,
  enableDeceasedReason = false,
  disallowMode,
  onSuccessfulSubmit,
}) => {
  const { useFlowActorRef } = useFlowHooks<FlowContext>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const [reasonState, sendToReasonMachine] = useMachine(
    reasonMachine as ReturnType<typeof createCancellationReasonMachine>,
  );
  const { label, businessUnit, customerId } = useDC();
  const [reasonsOptions, setReasonsOptions] = useState<{ value: string; label: string }[]>([]);
  const [contractRemainingMonths, setContractRemainingMonths] = useState(0);
  const [discontinueDisallowDialog, setDiscontinueDisallowDialog] = useState<
    '' | DC_Domain_Models_Products_DiscontinueDisallowReason
  >('');
  const [isDialogOpen, setDialogOpen] = useState(false);
  const { status: sessionStatus } = useSession();
  const isAuthenticated = sessionStatus === 'authenticated';

  useEffect(() => {
    if (sessionStatus === 'authenticated') {
      sendToReasonMachine({
        type: 'GET_REASONS',
        config: {
          businessUnit,
          label,
        },
        values: {
          customerId,
        },
      });
    } else if (sessionStatus === 'unauthenticated') {
      if (unauthenticatedAction === 'okta') {
        signIn('okta');
      } else {
        sendToFlowMachine({
          type: 'UPDATE_ROUTE',
          values: {
            path: '/inloggen/',
          },
        });
      }
    }
  }, [businessUnit, customerId, label, sendToFlowMachine, sendToReasonMachine, sessionStatus, unauthenticatedAction]);

  useEffect(() => {
    const { reasons, discontinueDisallowReason, remainingMonths } = reasonState.context;
    if (reasonState.matches('SUCCESS')) {
      if (disallowMode === 'dialog' && discontinueDisallowReason && discontinueDisallowReason.length > 0) {
        setDiscontinueDisallowDialog(discontinueDisallowReason as DC_Domain_Models_Products_DiscontinueDisallowReason);
        if (remainingMonths !== undefined) {
          setContractRemainingMonths(remainingMonths);
        }
        setDialogOpen(true);
      } else if (reasons) {
        setReasonsOptions(
          reasons.map(reason => ({
            value: reason.code ?? '',
            label: reason.text ?? '',
          })),
        );
      }
    }
  }, [disallowMode, reasonState]);

  const formSchema = yup.object({
    reason: enableDeceasedReason
      ? yup
          .string()
          .required(textualData?.reasonDetails?.reasonFormField?.value?.requiredMessage)
          .notOneOf([DECEASE_REASON_VALUE], textualData?.reasonDetails?.reasonFormField?.value?.requiredMessage)
      : yup.string().required(textualData?.reasonDetails?.reasonFormField?.value?.requiredMessage),
    explanation: yup
      .string()
      .when('reason', ([reason]) =>
        reason === OTHER_REASON_VALUE
          ? yup.string().required(textualData?.reasonDetails?.reasonTextFormField?.value?.requiredMessage)
          : yup.string(),
      ),
  });

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitSuccessful },
    watch,
  } = useForm<FormData>({
    mode: 'onSubmit',
    defaultValues: {
      reason: '',
      explanation: '',
    },
    resolver: typedYupResolver<FormData>(formSchema),
  });

  const selectedReason = watch('reason');

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();
      const selectedReasonLabel = reasonsOptions.find(option => option.value === formData.reason)?.label || '';
      onSuccessfulSubmit({
        reason: formData.reason,
        reasonLabel: selectedReasonLabel,
        explanation: formData.explanation,
        intake: reasonState.context.intake,
      });
    }
  }, [getValues, isSubmitSuccessful, onSuccessfulSubmit, reasonState.context.intake, reasonsOptions]);

  const dialogContent = getDialogContent(textualData?.dialogs, discontinueDisallowDialog, contractRemainingMonths);
  const showDisallowDialog = disallowMode === 'dialog' && discontinueDisallowDialog.length > 0;
  const isIntakeDisallowed = disallowMode === 'notification' && reasonState.matches('INTAKE_DISALLOWED');
  const hasApiError = reasonState.matches('ERROR');
  const disallowNotificationContent = getNotificationDisallowContent(
    textualData,
    reasonState.context.intake &&
      typeof reasonState.context.intake === 'object' &&
      'discontinueDisallowReason' in reasonState.context.intake
      ? (reasonState.context.intake.discontinueDisallowReason as string | undefined)
      : undefined,
  );

  if (unauthenticatedAction === 'login-route' && !isAuthenticated) {
    return null;
  }

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit} isLoading={reasonState.matches('FETCHING')}>
      {showDisallowDialog ? (
        <DisallowDialog
          title={dialogContent?.title}
          content={dialogContent?.content}
          triggerText={dialogContent?.triggerText}
          isOpen={isDialogOpen}
          setDialogOpen={setDialogOpen}
        />
      ) : (
        <>
          <Header>
            <Header.Title>{textualData?.content?.title?.value}</Header.Title>
          </Header>
          {isIntakeDisallowed ? (
            <Stack.Item>
              <NotificationBox
                isAlert
                title={disallowNotificationContent?.title}
                text={disallowNotificationContent?.content}
                variant="error"
              />
            </Stack.Item>
          ) : (
            <>
              {hasApiError ? (
                <Stack.Item>
                  <NotificationBox
                    isAlert
                    title={textualData?.notification?.errorNotification?.value?.title}
                    text={
                      textualData?.notification?.errorNotification?.value?.content ?? reasonState.context.errorMessage
                    }
                    variant={textualData?.notification?.errorNotification?.value?.variant ?? 'error'}
                  />
                </Stack.Item>
              ) : null}
              <Stack.Item>
                <Overview>
                  <Overview.Header>{textualData?.content?.text?.value}</Overview.Header>
                  <Overview.Content>
                    <Stack gap="4">
                      <Stack.Item as="fieldset">
                        <InputSelect
                          {...register('reason')}
                          label={textualData?.reasonDetails?.reasonFormField?.value?.label ?? ''}
                          placeholder={textualData?.reasonDetails?.reasonFormField?.value?.placeholder ?? ''}
                          hint={textualData?.reasonDetails?.reasonFormField?.value?.hint}
                          options={reasonsOptions}
                          error={errors.reason?.message}
                        />
                      </Stack.Item>

                      {selectedReason === OTHER_REASON_VALUE && (
                        <Stack.Item>
                          <InputText
                            {...register('explanation')}
                            label={textualData?.reasonDetails?.reasonTextFormField?.value?.label ?? ''}
                            placeholder={textualData?.reasonDetails?.reasonTextFormField?.value?.placeholder ?? ''}
                            hint={textualData?.reasonDetails?.reasonTextFormField?.value?.hint ?? ''}
                            error={errors.explanation?.message}
                          />
                        </Stack.Item>
                      )}
                    </Stack>
                  </Overview.Content>
                </Overview>
              </Stack.Item>
              {enableDeceasedReason && (
                <Stack.Item>
                  {selectedReason === DECEASE_REASON_VALUE && (
                    <Stack.Item>
                      <NotificationBox
                        isAlert={false}
                        text={<RichText html={textualData?.reasonDetails?.deceasedReasonContent?.value ?? ''} />}
                        title={textualData?.reasonDetails?.deceasedReasonTitle?.value}
                      />
                    </Stack.Item>
                  )}
                </Stack.Item>
              )}
              <Stack.Item>
                <NextButton alignX="end" isLoading={isSubmitSuccessful}>
                  {textualData?.content?.nextStepText?.value}
                </NextButton>
              </Stack.Item>
            </>
          )}
        </>
      )}
    </Layout.Content>
  );
};
